import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { CheckCircle, ArrowRight, Download, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useSubscriptionPermissions } from '../hooks/useSubscriptionPermissions';

const SuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, session, signOut } = useAuth();
  const { subscription, loading: subscriptionLoading } = useSubscriptionPermissions();
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [sessionData, setSessionData] = useState<any>(null);

  const sessionId = searchParams.get('session_id');
  const MAX_VERIFICATION_ATTEMPTS = 10; // 10 attempts = ~30 seconds

  // Verify user is still logged in
  useEffect(() => {
    if (!user || !session) {
      console.error('User not authenticated after checkout');
      toast.error('Session expirée. Veuillez vous reconnecter.');
      // Redirect to login with return URL
      navigate('/auth/signin?redirect=/success?session_id=' + sessionId);
      return;
    }
  }, [user, session, navigate, sessionId]);

  // Verify subscription was created and wait for webhook processing
  useEffect(() => {
    if (!sessionId || !user) return;

    setIsVerifying(true);
    let attempts = 0;
    let intervalId: NodeJS.Timeout | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    const checkSubscription = async (): Promise<boolean> => {
      attempts++;
      setVerificationAttempts(attempts);

      try {
        // Check user profile for subscription update
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('subscription_plan, subscription_started_at')
          .eq('user_id', user.id)
          .maybeSingle();

        if (profileError) {
          console.error('Error checking profile:', profileError);
          return false;
        }

        // Check if subscription was activated (plan should be set)
        if (profile && profile.subscription_plan && profile.subscription_plan !== 'launch') {
          console.log('✅ Subscription activated:', profile.subscription_plan);
          
          setSessionData({
            plan: profile.subscription_plan,
            startedAt: profile.subscription_started_at,
          });
          
          setIsVerifying(false);
          setIsLoading(false);
          toast.success('Abonnement activé avec succès !');
          return true;
        }

        // If max attempts reached, show warning but continue
        if (attempts >= MAX_VERIFICATION_ATTEMPTS) {
          console.warn('Max verification attempts reached. Subscription may still be processing.');
          setIsVerifying(false);
          setIsLoading(false);
          toast.warning('Vérification en cours. Votre abonnement sera activé sous peu.');
          return true; // Continue anyway
        }

        return false;
      } catch (error) {
        console.error('Error verifying subscription:', error);
        return false;
      }
    };

    // Initial check
    checkSubscription().then((verified) => {
      if (verified) return;

      // Poll every 3 seconds if not verified
      intervalId = setInterval(async () => {
        const verified = await checkSubscription();
        if (verified && intervalId) {
          clearInterval(intervalId);
        }
      }, 3000);
    });

    // Cleanup after max time (30 seconds)
    timeoutId = setTimeout(() => {
      if (intervalId) clearInterval(intervalId);
      setIsVerifying(false);
      setIsLoading(false);
      toast.warning('Vérification en cours. Votre abonnement sera activé sous peu.');
    }, 30000);

    // Cleanup function
    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sessionId, user]);

  if (isLoading || isVerifying || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {isVerifying ? 'Vérification de votre abonnement...' : 'Chargement...'}
          </h2>
          <p className="text-gray-600">
            {isVerifying 
              ? `Tentative ${verificationAttempts}/${MAX_VERIFICATION_ATTEMPTS} - Veuillez patienter`
              : 'Préparation de votre compte...'}
          </p>
        </div>
      </div>
    );
  }

  if (!sessionId) {
    toast.error('Aucun ID de session trouvé');
    navigate('/upgrade');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {sessionData?.plan === 'boost' || sessionData?.plan === 'scale' 
              ? 'Abonnement Activé !' 
              : 'Paiement Réussi !'}
          </h1>
          <p className="text-gray-600">
            {sessionData?.plan 
              ? `Votre abonnement ${sessionData.plan.toUpperCase()} est maintenant actif. Profitez de toutes les fonctionnalités premium !`
              : 'Merci pour votre achat. Vous avez maintenant accès à toutes les fonctionnalités premium.'}
          </p>
          {subscription && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Plan:</strong> {subscription.plan_name || 'N/A'}
              </p>
              {subscription.end_date && (
                <p className="text-sm text-blue-800 mt-1">
                  <strong>Expire le:</strong> {new Date(subscription.end_date).toLocaleDateString('fr-FR')}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-medium text-green-800 mb-2">Prochaines Étapes</h3>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• Accédez à votre tableau de bord immédiatement</li>
              <li>• Explorez toutes les fonctionnalités premium</li>
              <li>• Téléchargez votre facture</li>
            </ul>
          </div>

          <div className="flex flex-col space-y-3">
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full"
            >
              Aller au Tableau de Bord
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            
            <Button
              variant="outline"
              onClick={() => {
                // Ici vous pourriez générer et télécharger l'invoice
                toast.success('Facture téléchargée !');
              }}
              className="w-full"
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger la Facture
            </Button>
          </div>

          <p className="text-xs text-gray-500 mt-4">
            Un email de confirmation a été envoyé à votre adresse email enregistrée.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;